import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import _ from 'lodash'

//components
import { Grid } from '@mui/material';
import { Container, TableView, Link, SearchInput, IconButton, TableButtons, useTablePagination } from '@gpstrackit/gtc-react-controls';
import { LogManagerConsole } from 'Core/logManager';
import Preferences from 'Core/containers/Preferences';

//redux
import { sendGenericExport, catalogLoad, openDeleteTemplate } from 'Redux/actions';
import { reset, templateListSearch } from './redux/slice';
import { templateEditOpen } from '../../modals/edit/redux/slice';

//modals
import TempateEditModal from '../../modals/edit';
import TempateDeleteModal from '../../modals/delete';


export const ListView = () => {

	//constants
	const dispatch = useDispatch();
	const tableRef = useRef();
	const preferencesRef = useRef();
	const navigate = useNavigate();
	//language references
	const { messages, locale } = useIntl();

	//hook to handle access to data
	const pagination = useTablePagination({
		dispatch,
		action: templateListSearch,
		searchFields: ["firstName", "lastName"],
		tableRef: tableRef,
		sendGenericExport,
		exportName: "templateList",
	});

	//Redux States
	const user = useSelector(state => state.securityRedux.user);
	const { loading, items, total } = useSelector(state => state.templateListRedux);

	//Local States
	const [selectedItems, setSelectedItems] = useState([]);

	//callbackks
	const onChangeFilters = useCallback((filters) => {
	}, []);

	const refreshTable = useCallback(() => {
		setSelectedItems([]);
		pagination.current?.setPage(1);
		pagination.current?.setSelectedItems([]);
		pagination.onChangePage(1);

	})

	//effects
	//load effect
	useEffect(() => {
		LogManagerConsole("TEMPLATE", "ListView", "init");
		pagination.onChangeSort({ key: 'firstName', order: 'asc' });
		pagination.current?.setSelectedItems([]);

		return () => {
			LogManagerConsole("TEMPLATE", "ListView", "release");
			dispatch(reset())
		}

	}, []);


	//effect to load columns and language
	useEffect(() => {
		
		pagination.setColumns([
			{
				key: "firstName",
				column: "firstName",
				sort: true, //define if the column is going to be sortable
				show: true, //define if the column is default visible
				width: 250,
				title: messages["templateList_firstName"],
				export: true, //define if the column is going to be exported
				render: (item) => {
					return (
						<Grid container alignItems="center">
							<Link
								className="table-span"
								dest={`/app/template/detail/${item.id}`}
								onClick={() => navigate(`/app/template/detail/${item.id}`)}
								text={item?.firstName}
							/>
						</Grid>
					);
				},
				functionString: 'return row?.firstName'  //function to define the value of the column in the export, you can define a simple function in string
			},
			{
				key: "lastName",
				column: "lastName",
				sort: true, //define if the column is going to be sortable
				show: true, //define if the column is default visible
				width: 250,
				title: messages["templateList_lastName"],
				export: true, //define if the column is going to be exported
				functionString: 'return row?.lastName'  //function to define the value of the column in the export, you can define a simple function in string
			},
			{
				key: "unit",
				column: "unit.label",
				sort: false,
				show: true,
				width: 150,
				title: messages["templateList_unit"],
				export: true,
				render: (item) => item.unit?.label,
				functionString: 'return row?.unit?.label'
			},
			{
				key: "createdOn",
				column: "createdOn",
				sort: false,
				show: true,
				width: 150,
				title: messages["templateList_cretedOn"],
				export: true,
				render: (item) => item.createdOn,
				functionString: 'return row?.createdOn'
			}
		])
	}, [locale])

	const changePreferences = useCallback((newValues) => {
		const oldPreferences = preferencesRef?.current?.getPreferences("User", "templateList") || {}
		if (!_.isEqual(newValues, oldPreferences)) {
			const currentPreferences = { ...oldPreferences, ...newValues }
			preferencesRef.current.savePreference("User", "templateList", currentPreferences)
		}
	})


	return (
		<Container maxWidth={false} className="container-view">
			<Preferences
				ref={preferencesRef}
				preferencesKey="templateList"
				onInitialPreferences={(preferences) => {


				}}
			>
				<Grid
					container
					direction="row"
					style={{ padding: 15 }}
				>
					<Grid container direction="row" justifyContent="space-between" alignItems="center" >
						<Grid item xs={6} >
							<Grid
								container
								direction="row"
								justifyContent="flex-start"
								alignItems="center"
								spacing={1}
							>
								<Grid item style={{ display: 'flex' }}>
									<IconButton
										tooltip={messages["add"]}
										placement="bottom"
										variant="secondary"
										icon="add_box"
										size="small"
										disabled={loading}
										onClick={() => dispatch(templateEditOpen())}
									/>
									<IconButton
										tooltip={messages["templateList_otherActions"]}
										placement="bottom"
										variant="secondary"
										icon="assignment"
										size="small"
										disabled={loading}
									//onClick={() => }
									/>
									<IconButton
										tooltip={messages["delete"]}
										placement="bottom"
										// variant="secondary"
										variant={selectedItems.length === 0 ? "" : "secondary"}
										icon="delete"
										size="small"
										isDisabled={loading || selectedItems.length === 0}
										onClick={() => dispatch(openDeleteTemplate({ items: selectedItems }))}
									/>
									<TableButtons
										messages={messages}
										tableRef={tableRef}
										buttons={['export']}
										onExport={(type) => {
											pagination.onExport(type);
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={6} className="mb-10 text-right">
							<SearchInput
								placeholder={messages['search']}
								searchText={pagination.searchText}
								onSearchText={pagination.onSearchChange}
							/>
						</Grid>
					</Grid>
					<TableView
						ref={tableRef}
						keyColumn="id"
						data={items || []}
						total={total || 0}
						loading={loading}
						{...pagination}
						topheight={250}
						exportOrientation="landscape"
						hideDragable
						onSelectedItems={(items) => setSelectedItems(items)}
					/>
				</Grid>
			</Preferences>
			<TempateEditModal onRefresh={(refresh) => refresh && refreshTable} />
			<TempateDeleteModal onRefresh={(refresh) => refresh && refreshTable} />
		</Container>
	);
}

export default ListView;